'use client';

import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { GetCommentCount } from '@frameio/comments/components/Filters/components/OverflowMenu/hooks/gql/queries/generated/GetCommentCount';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { useCommentIdsFromMarkerUploadLazyQuery } from '@frameio/shared/integrations/gql';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { convertSecondsToMicroseconds } from '@frameio/utils/media/media';
import { useUxpHostInfoContext } from '../../../contexts/UxpHostInfoContext';
import { usePluginFeatures } from '../../usePluginFeatures';
import {
  type ExportRange,
  getCommentTimestampMicroseconds,
  isFiniteNumber,
  isMarkerInExportRange,
} from '../exportRange';
import { useCreateFrameCommentsFromMarkers } from '../useCreateCommentsFromMarkers';
import {
  filterDocumentMarkers,
  type HostMarkers,
  markerInitialText,
} from '../utils';

type ExportMarkersAsCommentsArgs = {
  assetId: string;
  /**
   * Host document id the markers belong to (Premiere sequence id, AE comp id).
   * Optional, included in telemetry only.
   */
  documentId?: string | null;
  /**
   * Host markers with `start` in **microseconds** on the source timeline. Both
   * Premiere (via {@link useDocumentMarkersMigrationQuery}) and After Effects
   * (via the dva-js AE resolver) already produce µs upstream, so callers pass
   * the markers straight through. `duration` stays in **seconds** to match the
   * Frame.io comment `duration` field; this hook converts it to microseconds
   * internally when computing the marker end for range filtering.
   */
  markers: HostMarkers[];
  /** Export window on the source timeline (µs). `null` = full timeline. */
  range: ExportRange | null;
  /**
   * When set (e.g. comp or sequence frame rate), marker timestamps that sit on
   * the frame grid are snapped so `Math.round` does not leave them slightly
   * below the intended frame for downstream `floor(time × fps)` display.
   */
  framesPerSecond?: number | null;
};

const markerDurationMicroseconds = (marker: HostMarkers): number => {
  if (!isFiniteNumber(marker.duration)) return 0;
  return convertSecondsToMicroseconds(marker.duration) ?? 0;
};

/**
 * Shared filter + map + send + log pipeline for the marker-to-comment export
 * flows in Premiere and After Effects. Each host hook owns the upstream
 * concerns (fetching markers, sourcing the export range, unit normalization)
 * and then delegates here.
 *
 * Telemetry keys are intentionally host-agnostic
 * (`integrations-marker-comments-*`); the host name is read from
 * {@link useUxpHostInfoContext} and added to every log payload as `hostName`,
 * so dashboards/monitors can split on it. These replace the pre-consolidation
 * per-host keys, kept here for searchability when migrating Datadog
 * dashboards/monitors:
 * - `integrations-premiere-marker-comments-error`
 * - `integrations-premiere-marker-comments-partial-failure`
 * - `integrations-ae-marker-comments-create-partial-failure`
 *
 * Behavior:
 * 1. Drops markers via {@link filterDocumentMarkers} (deleted, already-stamped,
 *    already-linked).
 * 2. Drops markers outside `range` (`null` = full timeline; see
 *    {@link isMarkerInExportRange}).
 * 3. Builds `CommentFromMarkerPayload[]` with timestamps offset by
 *    `range.startMicroseconds` (clamped at 0). When `framesPerSecond` is set,
 *    frame-grid markers are snapped so integer µs stay inside the intended
 *    frame for downstream frame math.
 * 4. Sends them via the existing
 *    {@link useCreateFrameCommentsFromMarkers}.
 * 5. Logs partial failures and unexpected errors to Datadog. Also `warn`s on
 *    individual marker anomalies (non-finite `start`, negative offsets) so
 *    upstream unit/normalization regressions surface instead of silently
 *    pinning comments to `t=0`.
 */
export const useExportMarkersAsComments = () => {
  const createFrameCommentsFromMarkers = useCreateFrameCommentsFromMarkers();
  const { name: hostName } = useUxpHostInfoContext();
  const currentUserId = useGetActiveUserId();
  const apolloClient = useApolloClient();
  const { canUseHiddenMarkerUploadIds } = usePluginFeatures();
  const [getCommentIdsFromMarkerUpload] =
    useCommentIdsFromMarkerUploadLazyQuery({
      context: { resolveWithUXP: true },
      fetchPolicy: 'network-only',
    });

  return useCallback(
    async ({
      assetId,
      documentId,
      markers,
      range,
      framesPerSecond,
    }: ExportMarkersAsCommentsArgs) => {
      if (!assetId || !markers.length) return { commentIds: [] };

      let registeredUploadCommentIds: ReadonlySet<string> | undefined;
      if (canUseHiddenMarkerUploadIds) {
        try {
          const { data } = await getCommentIdsFromMarkerUpload({
            variables: { assetId },
          });
          registeredUploadCommentIds = new Set(
            data?.commentIdsFromMarkerUpload ?? []
          );
        } catch (error: unknown) {
          datadogLogger.warn(
            'integrations-marker-comments-upload-registry-load-failed',
            {
              assetId,
              documentId,
              error: error instanceof Error ? error.message : String(error),
              hostName,
            }
          );
        }
      }

      const markerStartMicroseconds = (marker: HostMarkers): number => {
        if (isFiniteNumber(marker.start)) return marker.start;
        datadogLogger.warn(
          'integrations-marker-comments-invalid-marker-start',
          {
            assetId,
            documentId,
            hostName,
            markerId: marker.id ?? null,
            rawStart: marker.start ?? null,
          }
        );
        return 0;
      };

      const validMarkers = filterDocumentMarkers(markers, currentUserId, {
        registeredUploadCommentIds,
      })
        .map((marker) => {
          const start = markerStartMicroseconds(marker);
          return {
            marker,
            start,
            end: start + markerDurationMicroseconds(marker),
          };
        })
        .filter(({ start, end }) => isMarkerInExportRange(start, end, range));

      if (!validMarkers.length) {
        datadogLogger.warn(
          'integrations-marker-comments-all-markers-filtered',
          {
            assetId,
            documentId,
            hostName,
            inputMarkerCount: markers.length,
            linkedMarkerCount: markers.filter((m) => m.frameCommentId).length,
          }
        );
        return { commentIds: [] };
      }

      const payloads = validMarkers.map(({ marker, start }) => {
        if (range && start < range.startMicroseconds) {
          datadogLogger.warn('integrations-marker-comments-negative-offset', {
            assetId,
            documentId,
            hostName,
            markerId: marker.id ?? null,
            markerStartMicroseconds: start,
            rangeStartMicroseconds: range.startMicroseconds,
          });
        }
        return {
          duration: marker.duration,
          initialText: markerInitialText(marker),
          markerId: marker.id ?? null,
          timestampMicroseconds: getCommentTimestampMicroseconds(
            start,
            range,
            framesPerSecond
          ),
        };
      });

      let createdAtLeastOneComment = false;
      let commentIds: string[] = [];
      try {
        const result = await createFrameCommentsFromMarkers(assetId, payloads, {
          documentId,
        });
        commentIds = result.commentIds;
        const { failures } = result;

        if (failures.length > 0) {
          datadogLogger.error('integrations-marker-comments-partial-failure', {
            assetId,
            attemptedCommentCount: payloads.length,
            documentId,
            errorMessages: failures.map((f) => f.message),
            failureCount: failures.length,
            hostName,
          });
        }

        createdAtLeastOneComment = commentIds.length > 0;
      } catch (error: unknown) {
        datadogLogger.error('integrations-marker-comments-error', {
          assetId,
          documentId,
          error: error instanceof Error ? error.message : String(error),
          hostName,
        });
        return { commentIds: [] };
      }

      if (!createdAtLeastOneComment) return { commentIds: [] };

      // Refetch `commentCount` on the asset so badges (e.g. composition list
      // thumbnails) reflect every newly-created comment.
      try {
        await apolloClient.query({
          query: GetCommentCount,
          variables: { assetId },
          fetchPolicy: 'network-only',
        });
      } catch (error: unknown) {
        datadogLogger.warn(
          'integrations-marker-comments-count-refetch-failed',
          {
            assetId,
            documentId,
            error: error instanceof Error ? error.message : String(error),
            hostName,
          }
        );
      }

      return { commentIds };
    },
    [
      apolloClient,
      canUseHiddenMarkerUploadIds,
      createFrameCommentsFromMarkers,
      currentUserId,
      getCommentIdsFromMarkerUpload,
      hostName,
    ]
  );
};
